import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Form, Switch, message } from 'antd';
import MailRecipientsField from '@/components/email-templates/MailRecipientsField';
import { mailRecipientsToForm } from '@/components/email-templates/mailRecipientsForm';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import { HowToCard } from '@/components/how-to-card';
import { getZazSettings, updateZazSettings } from '@/services/zaz.service';
import { ZAZ_SETTINGS_QUERY_KEY } from './zazKeys';

const GUIDE_ICONS = [SendOutlined, EditOutlined, RollbackOutlined, FilePdfOutlined];

/**
 * HR tile of the company profile: whether HR sends a ZAZ (request for hiring)
 * about new employees to the heads of their sector, and which optional
 * fields the company does not use. Off by default. Saves on its own.
 */
export default function ZazSettingsCard({ companyId, readOnly = false }) {
  const { t } = useTranslation(['zaz', 'forms']);
  const { tn } = useEmployeeNoticesT();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const enabled = Form.useWatch('enabled', form);

  const { data } = useQuery({
    queryKey: [ZAZ_SETTINGS_QUERY_KEY, companyId],
    queryFn: () => getZazSettings(companyId),
    enabled: !!companyId,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        enabled: !!data.enabled,
        hiddenFields: data.hiddenFields || [],
        mailRecipients: mailRecipientsToForm(data.mailRecipients),
      });
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: (values) =>
      updateZazSettings({
        company: companyId,
        enabled: !!values.enabled,
        hiddenFields: values.hiddenFields || [],
        mailRecipients: values.mailRecipients,
      }),
    onSuccess: (result) => {
      message.success(result?.message || t('settingsSaved'));
      queryClient.invalidateQueries({
        queryKey: [ZAZ_SETTINGS_QUERY_KEY, companyId],
      });
    },
    onError: (error) => message.error(errorText(error, t('saveFailed'))),
  });

  // Refused (a role without these settings): nothing to show.
  if (!data) return null;

  const guideSteps = t('guide.steps', { returnObjects: true });
  const showSave = !readOnly && (enabled || data.enabled);

  return (
    <section className="mt-3 grid gap-2 rounded-xl border border-zinc-200 bg-white p-3">
      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        onFinish={() => save.mutate(form.getFieldsValue(true))}
        autoComplete="off"
      >
        <div className="flex items-center gap-2">
          <FileDoneOutlined className="text-zinc-500" />
          <h3 className="m-0 text-[13px] font-bold text-zinc-900">
            {t('settingsTitle')}
          </h3>
          <Form.Item name="enabled" valuePropName="checked" noStyle>
            <Switch size="small" className="!ml-auto" />
          </Form.Item>
        </div>

        {enabled && (
          <div className="mt-3">
            <HowToCard
              title={t('guide.title')}
              open={!data.enabled}
              sections={[
                {
                  title: t('guide.sectionTitle'),
                  steps: (Array.isArray(guideSteps) ? guideSteps : []).map(
                    (step, i) => ({ ...step, icon: GUIDE_ICONS[i] })
                  ),
                },
              ]}
            />
            <div className="mb-1 text-[12.5px] font-bold text-zinc-700">
              {t('settingsHidden')}
            </div>
            <p className="mb-2 text-[12px] text-zinc-500">
              {t('settingsHiddenHint')}
            </p>
            <Form.Item name="hiddenFields" className="!mb-0">
              <Checkbox.Group className="grid gap-1 sm:grid-cols-2">
                {(data.hideableFields || []).map((key) => (
                  <Checkbox key={key} value={key}>
                    {t(`fields.${key}`)}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
            <MailRecipientsField companyId={companyId} hint={tn('mailRecipients.hintZaz')} />
          </div>
        )}

        {showSave && (
          <div className="mt-3 flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              className="!rounded-[50px]"
              loading={save.isPending}
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        )}
      </Form>
    </section>
  );
}
